/**
 * 24-hour strip: flat muted = window closed, light = open, primary bar =
 * the container span with one tick per slot. Hour gridlines carry labels
 * anchored to their real positions; a legend states the exact times so the
 * strip never has to be guessed at.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';

import { Tooltip } from '@/components/ui';

import { windowText } from '../../utils/clock/dk-label';
import { useCountryClock } from './time/country-clock-context';
import { SpanLabels } from './SpanLabels';
import { rangeSegments, tickPositions } from '../../utils/day-strip';

const HOUR_MARKS = [
  { pct: 0, label: '00:00', align: 'left' },
  { pct: 25, label: '06:00', align: 'center' },
  { pct: 50, label: '12:00', align: 'center' },
  { pct: 75, label: '18:00', align: 'center' },
  { pct: 100, label: '24:00', align: 'right' },
] as const;

interface Props {
  windowStartMin: number;
  windowEndMin: number;
  recipientTimezone?: string | null;
  spanStartMin: number | null;
  spanEndMin: number | null;
  spanLabel?: string;
  slotMinutes: number[];
  /** Same order as slotMinutes — shown when hovering a tick. */
  slotLabels?: string[];
}

export const DayStrip: FC<Props> = ({
  windowStartMin,
  windowEndMin,
  recipientTimezone = null,
  spanStartMin,
  spanEndMin,
  spanLabel,
  slotMinutes,
  slotLabels,
}) => {
  const clock = useCountryClock(); // the legend reads Danish first (15/10/2026); the strip stays the country's day
  const windowSegs = rangeSegments(windowStartMin, windowEndMin);
  const spanSegs =
    spanStartMin !== null && spanEndMin !== null && spanEndMin > spanStartMin
      ? rangeSegments(spanStartMin, spanEndMin)
      : [];
  const hasSpan =
    spanStartMin !== null && spanEndMin !== null && spanEndMin > spanStartMin;
  return (
    <div className="flex w-full flex-col gap-1">
      {hasSpan ? (
        <SpanLabels startMin={spanStartMin} endMin={spanEndMin} />
      ) : null}
      <div className="relative h-8 overflow-hidden rounded-lg border border-border/60 bg-muted/60">
        {windowSegs.map((seg, i) => (
          <div
            key={`w${i}`}
            className="absolute inset-y-0 bg-card"
            style={{ left: `${seg.leftPct}%`, width: `${seg.widthPct}%` }}
          />
        ))}
        {[25, 50, 75].map((pct) => (
          <div
            key={pct}
            className="absolute inset-y-0 w-px bg-border/60"
            style={{ left: `${pct}%` }}
          />
        ))}
        {spanSegs.map((seg, i) => (
          <div
            key={`s${i}`}
            className="absolute inset-y-[7px] rounded-md border border-primary/25 bg-primary/[0.07] transition-[left,width] duration-500 ease-out motion-reduce:transition-none"
            style={{ left: `${seg.leftPct}%`, width: `${seg.widthPct}%` }}
            title={spanLabel}
          />
        ))}
        {tickPositions(slotMinutes).map((leftPct, i) => (
          <span
            key={`t${i}`}
            className="absolute inset-y-0 flex w-3 -translate-x-1/2 cursor-default items-center justify-center transition-[left] duration-500 ease-out motion-reduce:transition-none"
            style={{ left: `${leftPct}%` }}
          >
            {slotLabels?.[i] ? (
              <Tooltip content={slotLabels[i]}>
                <span className="block h-[12px] w-[3px] rounded-full bg-primary/80 transition-all duration-300 animate-in fade-in-0 zoom-in-50 hover:scale-x-150 hover:bg-primary motion-reduce:animate-none" />
              </Tooltip>
            ) : (
              <span className="block h-[12px] w-[3px] rounded-full bg-primary/80 duration-300 animate-in fade-in-0 zoom-in-50 motion-reduce:animate-none" />
            )}
          </span>
        ))}
      </div>
      <div className="relative h-4 font-mono text-[10px] tabular-nums text-muted-foreground">
        {HOUR_MARKS.map((mark) => (
          <span
            key={mark.label}
            className={
              mark.align === 'center'
                ? 'absolute -translate-x-1/2'
                : mark.align === 'right'
                  ? 'absolute -translate-x-full'
                  : 'absolute'
            }
            style={{ left: `${mark.pct}%` }}
          >
            {mark.label}
          </span>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-[11px] text-muted-foreground">
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-5 rounded-sm border border-border/60 bg-card" />
          window {windowText(windowStartMin, windowEndMin, { ...clock, tz: clock.tz ?? recipientTimezone })}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="relative h-2.5 w-5 rounded-sm border border-primary/40 bg-primary/10">
            <span className="absolute inset-y-0.5 left-1/2 w-[2px] -translate-x-1/2 rounded-full bg-primary" />
          </span>
          {spanLabel ?? 'container span'}
        </span>
      </div>
    </div>
  );
};
